<template>
  <div>
    <b-tabs v-model="currentStep" nav-class="custom-tabs" active-nav-item-class="active-tab" pills>
      <b-tab @click.prevent="preventTabSwitch(0)">
        <template #title>
          <div class="d-flex align-items-center" style="gap: 10px;">
            <div class="step-number" :class="{ valid: stepsValidation[0] }">
              <img v-if="stepsValidation[0]" src="@/assets/img/icons/tick-outline.svg" alt="">
              <span v-else>1</span>
            </div>
            <div class="step-description">
              <div v-if="isPersonalAccount">
                <p class="bold">{{ $t("views.seller.my_account.text_1262") }}</p>
                <p style="font-size: 13px;">{{ $t("views.seller.my_account.text_1267") }}</p>
              </div>
              <div v-if="isBusinessAccount">
                <p class="bold">{{ $t("views.seller.my_account.text_1320") }}</p>
                <p style="font-size: 13px;">{{ $t("views.seller.my_account.text_1321") }}</p>
              </div>
            </div>
          </div>
        </template>
        <div>
          <PersonalDataFormStep v-if="isPersonalAccount" :user="user" ref="personalDataStep"/>
          <BusinessDataFormStep v-if="isBusinessAccount" :company="company" ref="businessDataStep"/>
        </div>
      </b-tab>

      <!-- Etapa 2 -->
      <b-tab @click.prevent="preventTabSwitch(0)">
        <template #title>
          <div class="d-flex align-items-center" style="gap: 10px;">
            <div class="step-number" :class="{ valid: stepsValidation[1] }">
              <img v-if="stepsValidation[1]" src="@/assets/img/icons/tick-outline.svg" alt="">
              <span v-else>2</span>
            </div>
            <div class="step-description">
              <p class="bold">{{ $t("views.seller.my_account.text_1263") }}</p>
              <p style="font-size: 13px;">{{ $t("views.seller.my_account.text_1264") }}</p>
            </div>
          </div>
        </template>
        <div>
          <IdentityVerificationFormStep :user="user" ref="identityVerificationStep"/>
        </div>
      </b-tab>

      <!-- Finalizar -->
      <b-tab @click.prevent="preventTabSwitch(0)">
        <template #title>
          <div class="d-flex align-items-center" style="gap: 10px;">
            <div class="step-number" :class="{ valid: stepsValidation[2] }">
              <img v-if="stepsValidation[2]" src="@/assets/img/icons/tick-outline.svg" alt="">
              <span v-else>3</span>
            </div>
            <div class="step-description">
              <p class="bold">{{ $t("views.seller.my_account.text_1265") }}</p>
              <p style="font-size: 13px;">{{ $t("views.seller.my_account.text_1266") }}</p>
            </div>
          </div>
        </template>
        <div>
          <BankAccountFormStep :user="user" ref="bankAccountStep"/>
        </div>
      </b-tab>
    </b-tabs>
    <div class="divider"></div>
    <div class="d-flex justify-content-end" style="gap: 16px;">
      <BaseNewButton variant="secondary" icon="arrow-left" iconPosition="left" type="button" @click="prevStep">{{
        $t("views.seller.my_account.text_1308")
      }}</BaseNewButton>
      <BaseNewButton variant="secondary" icon="tick" iconPosition="left" type="submit" @click="handleSave">{{
        $t("views.seller.my_account.text_1174")
      }}</BaseNewButton>
      <BaseNewButton v-if="!(modeEdit && currentStep === 2)" variant="primary" icon="arrow-right-outline" iconPosition="right" type="button" @click="nextStep">{{
        currentStep === 2 ? $t("views.seller.my_account.text_1309") : $t("views.seller.my_account.text_1243")
      }}</BaseNewButton>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import PersonalDataFormStep from './PersonalDataFormStep.vue';
import IdentityVerificationFormStep from './IdentityVerificationFormStep.vue';
import BankAccountFormStep from './BankAccountFormStep.vue';
import BusinessDataFormStep from './BusinessDataFormStep.vue';
import SubAccountService from '@/services/resources/SubAccountService';

const subAccountService = SubAccountService.build();

export default {
  components: {
    PersonalDataFormStep,
    IdentityVerificationFormStep,
    BankAccountFormStep,
    BusinessDataFormStep
  },
  props: {
    accountType: {
      type: String,
      required: true
    },
  },
  data() {
    return {
      currentStep: 0,
      user: {},
      company: {},
      handleSubmit: () => {},
      stepsValidation: [false, false, false],
      stepsSaved: [false, false, false],
      modeEdit: false
    };
  },
  watch: {
    "$root.currentStep": {
      handler(newValue, oldValue) {
        this.currentStep = newValue;
      },
      deep: true, // Se for um objeto e quiser detectar mudanças internas
      immediate: true, // Se quiser executar logo que o componente for montado
    },
    "$root.modeEdit": {
      handler(newValue, oldValue) {
        this.modeEdit = newValue;
      },
      deep: true, // Se for um objeto e quiser detectar mudanças internas
      immediate: true, // Se quiser executar logo que o componente for montado
    },
  },
  computed: {
    activeFormStep() {
      const refs = ['personalDataStep', 'identityVerificationStep', 'bankAccountStep'];
      
      if (this.isBusinessAccount) {
        refs[0] = 'businessDataStep';
      }
      return this.$refs[refs[this.currentStep]];
    },
    isPersonalAccount() {
      return this.accountType === 'personal_account';
    },
    isBusinessAccount() {
      return this.accountType === 'legal_account';
    }
  },
  methods: {
    async nextStep() {
      const validated = await this.activeFormStep.validateForm();

      if (!validated) {
        return;
      }
      
      if (!this.stepsSaved[this.currentStep] && !this.modeEdit) {
        this.$bvToast.toast(this.$t('views.seller.my_account.text_1310'), {
          title: this.$t('views.seller.my_account.text_1311'),
          variant: 'danger',
          autoHideDelay: 5000,
          appendToast: true,
        });
        return;
      }

      this.stepsValidation[this.currentStep] = validated;

      const allStepsSaved = this.stepsSaved.filter(step => step);

      if (this.currentStep === 2 && allStepsSaved.length === 3) {
        this.handleSend();
      }

      if (this.currentStep < 2 && validated) {
        this.currentStep++;
      }
    },
    prevStep() {
      if (this.currentStep === 0) {
        this.$emit('back', { modeEdit: false });
      }
      if (this.currentStep > 0) {
        this.stepsValidation[this.currentStep] = false;
        this.currentStep--;
      }
    },
    async handleSend() {
      try {
        this.$root.$emit('on-saving', true);

        await subAccountService.create();
        
        this.$root.$emit('on-finish', true);
      } catch (error) {
        console.log(error);
      } finally {
        this.$root.$emit('on-saving', false);
      }
    },
    async handleSave() {
      try {
        const validated = await this.activeFormStep.validateForm();

        if (!validated) {
          return;
        }
        
        let data = await this.activeFormStep.handleSubmit();

        /** Quando o data vier null significa que o submit é feito dentro do component de step atual */
        if (!data) {
          this.setStepSaved();
          return;
        }

        /** Salva somente os dados da empresa reaproveitando o método onSubmit do componente MyAccount */
        if (data.company) {
          data = {
            ...this.user,
            company: data.company
          };
          this.handleSubmit('company', data);
          this.setStepSaved();

          if (this.modeEdit) {
            this.$root.$emit('on-finish', true);
          }
          return;
        }

        /** Salva os dados do usuário reaproveitando o método onSubmit do componente MyAccount */
        if (data.birth_date) {
          const birthDate = moment(this.user.birth_date, 'DD/MM/YYYY', true);
          data.birth_date = birthDate.format('YYYY-MM-DD', true);
        }
        
        this.handleSubmit('user', data); 
        this.setStepSaved();
        
        if (this.modeEdit) {
          this.$root.$emit('on-finish', true);
        }
      } catch (error) {
        console.log('FinancialDataFormSteps - handleSave:', error);
      }
    },
    preventTabSwitch(index) {
    },
    setStepSaved() {
      this.stepsSaved[this.currentStep] = true;
    }
  },
  mounted() {
    if (!this.$root.myAccountData) {
      return;
    }

    const { myAccountData } = this.$root;

    this.user = Object.assign({}, myAccountData.user);
    this.company = Object.assign({}, myAccountData.company);

    if (myAccountData.user.birth_date) {
      this.user.birth_date = moment(myAccountData.user.birth_date, 'YYYY-MM-DD').format('DD/MM/YYYY');
    }
    this.handleSubmit = myAccountData.onSubmit;

    if (this.$root.currentStep) {
      this.currentStep = this.$root.currentStep;
    }
  }
};
</script>

<style>
#financial-data .tabs {
  display: flex;
  flex-direction: column;
  gap: 48px;
}

.custom-tabs {
  display: flex;
  justify-content: space-between;
}

.custom-tabs .nav-link {
  background-color: #fff !important;
  padding: 0;
  display: block;
  pointer-events: none;
}

.custom-tabs .nav-link .step-number {
  display: flex;
  width: 40px;
  height: 40px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 20px;
  background: var(--Default-primary-800, #EDF9FF);
}

.custom-tabs .nav-link .step-number span {
  color: var(--Default-primary-800, #0854C5);
}

.custom-tabs .nav-link.active-tab .step-number {
  background: var(--Default-primary-800, #0854C5);
}

.custom-tabs .nav-link.active-tab .step-number span {
  color: #fff;
}

.custom-tabs .nav-link .step-number.valid {
  background: var(--Default-primary-800, #0854C5);
  color: #fff;
}

</style>
